/**
 * NetWorthTab - Net worth tracking and visualization component
 * Redesigned with new hero card and modern list items
 */
import { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import customToast from "../../utils/customToast";
import { 
  Plus, TrendingUp, 
  Building2, Wallet, CreditCard,
  HelpCircle, Check, Edit3, Info,
  BarChart3, PiggyBank, Receipt, Trash2
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { formatCurrency, getCurrentCurrency } from "../../utils/formatters";
import { getMonthKey } from "../../utils/dateHelpers";
import { getAccentColors } from "../../utils/themes";
import { THEME_COLORS } from "../../utils/constants";
import { NetWorthChartFromMonthly } from "../charts/TrendCharts";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { PremiumFeature } from "../premium/PremiumComponents";
import NetWorthHero from "../networth/NetWorthHero";
import { usePlan, useMoneyPlanShown } from "../../utils/plan"; // plan_09, w2_01
import { WorthPath, recentPace } from "../wealth/WealthPaths"; // plan_22, w2_04
import { planGrowth } from "../moneyPlan/MoneyPlan"; // plan_22
import { countsInProjection, signOf } from "../../utils/futureIncome"; // plan_25, plan_28
import { getNetWorthFunds, countExcludedFunds } from "../../utils/netWorthMath";
import { investmentBalance, latestManual, manualDayFor, localDay } from "../../utils/investMath"; // dark_13: one sum for investment balances

const NetWorthTab = ({ sinkingFunds = [], extraPayments = [], investmentAccounts = [], userAssets = [], userDebts = [], transactions = [], budgetSettings = {}, saveUserData, theme = 'pink', embedded = false, viewMonth: externalViewMonth, onNavigateMonth, showNetWorthInfo: externalShowNetWorthInfo, onCloseNetWorthInfo, onShowInfo, previousNetWorth = null, userData = null }) => {
  const { hasAccess, showFeaturePaywall } = useSubscription();
  const canViewHistory = hasAccess('netWorthHistory');
  const canViewTrends = hasAccess('netWorthTrends');

  const [internalViewMonth, setInternalViewMonth] = useState(new Date());
  const viewMonth = (embedded && externalViewMonth) ? externalViewMonth : internalViewMonth;
  const [internalShowNetWorthInfo, setInternalShowNetWorthInfo] = useState(false);
  const showNetWorthInfo = (embedded && externalShowNetWorthInfo !== undefined) ? externalShowNetWorthInfo : internalShowNetWorthInfo;
  const closeNetWorthInfo = () => {
    if (embedded && onCloseNetWorthInfo) {
      onCloseNetWorthInfo();
      return;
    }
    setInternalShowNetWorthInfo(false);
  };
  const [editingItem, setEditingItem] = useState(null);
  const [goalInput, setGoalInput] = useState('');
  const [currentInput, setCurrentInput] = useState('');
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [showAddDebt, setShowAddDebt] = useState(false);
  const [newAsset, setNewAsset] = useState({ name: '', goal: '', current: '' });
  const [newDebt, setNewDebt] = useState({ name: '', original: '', current: '' });
  const [showBalanceBreakdown, setShowBalanceBreakdown] = useState(null);
  const [editingInvestmentBalance, setEditingInvestmentBalance] = useState(null);
  const [overrideBalanceInput, setOverrideBalanceInput] = useState('');
  const [viewingDebtPayments, setViewingDebtPayments] = useState(null); // debt id to show payments for

  // Get theme-aware accent colors
  const accentColors = getAccentColors(theme);
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  const plan = usePlan(); // plan_09
  const moneyPlanShown = useMoneyPlanShown(); // w2_01
  // plan_22: how your net worth grows at your money plan's pace
  const planGrow = useMemo(() => (plan && moneyPlanShown && userData ? planGrowth(userData.money_diary && userData.money_diary.plan, userData.money_diary || {}, userData) : null), [plan, moneyPlanShown, userData]);
  // w2_04: no money plan? The dates are an estimate from the last 6 months.
  const recentGrow = useMemo(() => (plan && !planGrow ? recentPace(transactions) : 0), [plan, planGrow, transactions]);
  const worthGrow = planGrow || (recentGrow > 0 ? (k) => recentGrow * k : null);

  const monthKey = getMonthKey(viewMonth);
  const monthName = viewMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const currentDate = new Date();
  const isCurrentMonth = viewMonth.getMonth() === currentDate.getMonth() && viewMonth.getFullYear() === currentDate.getFullYear();

  // Initialize investment accounts - use empty array if none set
  const investments = investmentAccounts.length > 0 ? investmentAccounts : [];
  const assets = userAssets || [];
  const debts = userDebts || [];

  // Get the end of the viewed month for filtering transactions
  // For current month, use today's date; for past months, use end of that month
  const getViewMonthCutoff = () => {
    if (isCurrentMonth) {
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      return now;
    }
    return new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0, 23, 59, 59);
  };
  const viewMonthCutoff = getViewMonthCutoff();

  // Filter transactions to only include those up to the cutoff date
  const getTransactionsUpToViewMonth = (allTransactions) => {
    return allTransactions.filter(t => {
      const txDate = new Date(t.date);
      return txDate <= viewMonthCutoff;
    });
  };

  // Calculate balance based on transactions for sinking funds
  const getFundBalance = (fundId) => {
    const fund = (sinkingFunds || []).find(f => f.id === fundId);
    const startingBalance = fund?.starting_balance || 0;
    
    const relevantTransactions = getTransactionsUpToViewMonth(transactions);
    
    const deposits = relevantTransactions
      .filter(t => t.account_id === fundId && t.account_type === 'fund' && t.transaction_type === 'deposit')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const withdrawals = relevantTransactions
      .filter(t => t.account_id === fundId && t.account_type === 'fund' && t.transaction_type === 'withdrawal')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const interest = relevantTransactions
      .filter(t => t.interest_account_id === fundId && t.interest_account_type === 'fund' && t.is_interest)
      .reduce((sum, t) => sum + t.amount, 0);
    
    return startingBalance + deposits + interest - withdrawals;
  };

  // Calculate balance for investment accounts
  const getInvestmentBalance = (invId) => {
    const inv = investments.find(i => i.id === invId);
    return investmentBalance(inv, transactions, monthKey, { cutoffDay: localDay(viewMonthCutoff) }); // dark_13
  };

  // Check if an investment has override for current month
  const hasOverride = (invId) => !!latestManual(investments.find(i => i.id === invId), monthKey); // dark_13

  // Get calculated balance (ignoring override)
  const getCalculatedInvestmentBalance = (invId) => {
    const inv = investments.find(i => i.id === invId);
    const startingBalance = inv?.starting_balance || 0;
    
    const relevantTransactions = getTransactionsUpToViewMonth(transactions);
    
    const deposits = relevantTransactions
      .filter(t => t.account_id === invId && t.account_type === 'investment' && t.transaction_type === 'deposit')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const withdrawals = relevantTransactions
      .filter(t => t.account_id === invId && t.account_type === 'investment' && t.transaction_type === 'withdrawal')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const interest = relevantTransactions
      .filter(t => t.interest_account_id === invId && t.interest_account_type === 'investment' && t.is_interest)
      .reduce((sum, t) => sum + t.amount, 0);
    
    return startingBalance + deposits + interest - withdrawals;
  };

  // Get balance breakdown
  const getAccountBreakdown = (accountId, accountType) => {
    const startingBalance = accountType === 'fund' 
      ? (sinkingFunds || []).find(f => f.id === accountId)?.starting_balance || 0
      : investments.find(i => i.id === accountId)?.starting_balance || 0;
    
    const relevantTransactions = getTransactionsUpToViewMonth(transactions);
    
    const deposits = relevantTransactions
      .filter(t => t.account_id === accountId && t.account_type === accountType && t.transaction_type === 'deposit')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const withdrawals = relevantTransactions
      .filter(t => t.account_id === accountId && t.account_type === accountType && t.transaction_type === 'withdrawal')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const interest = relevantTransactions
      .filter(t => t.interest_account_id === accountId && t.interest_account_type === accountType && t.is_interest)
      .reduce((sum, t) => sum + t.amount, 0);
    
    return { startingBalance, deposits, withdrawals, interest };
  };

  // Get asset value for the viewed month
  // Past months: only show their own explicit value or starting_balance
  // Current/future months: only carry forward from edits made at currentMonth or later
  const currentMonthKeyNow = getMonthKey(new Date());
  const getCurrentForMonth = (item) => {
    // If there's an exact entry for this month, use it
    if (item.current_by_month?.[monthKey] !== undefined) {
      return item.current_by_month[monthKey];
    }
    // For current month or future: only carry forward from entries >= current month
    if (monthKey >= currentMonthKeyNow) {
      const entries = Object.keys(item.current_by_month || {}).sort();
      const carryable = entries.filter(k => k >= currentMonthKeyNow && k <= monthKey);
      if (carryable.length > 0) {
        return item.current_by_month[carryable[carryable.length - 1]];
      }
    }
    // No relevant entry — use starting_balance from onboarding
    return item.starting_balance || 0;
  };

  // Calculate debt balance including payments
  const getDebtBalance = (debt) => {
    // Get the starting balance - prefer the new starting_balance field, fall back to current_by_month for legacy data
    let startingBalance;
    if (debt.starting_balance !== undefined) {
      startingBalance = debt.starting_balance;
    } else {
      // Legacy: use current_by_month
      const monthKeys = Object.keys(debt.current_by_month || {}).sort();
      startingBalance = monthKeys.length > 0 
        ? debt.current_by_month[monthKeys[monthKeys.length - 1]] 
        : (debt.original_amount || 0);
    }
    
    // Sum all debt payments for this debt UP TO the viewed month
    const relevantTransactions = getTransactionsUpToViewMonth(transactions || []);
    const totalPayments = relevantTransactions
      .filter(t => t.is_debt_payment && t.debt_id === debt.id)
      .reduce((sum, t) => sum + t.amount, 0);
    
    return Math.max(0, startingBalance - totalPayments);
  };

  // Get total payments for a debt up to the viewed month
  const getDebtTotalPayments = (debtId) => {
    return getTransactionsUpToViewMonth(transactions || [])
      .filter(t => t.is_debt_payment && t.debt_id === debtId)
      .reduce((sum, t) => sum + t.amount, 0);
  };

  // Get starting balance for a debt (for editing)
  const getDebtStartingBalance = (debt) => {
    if (debt.starting_balance !== undefined) {
      return debt.starting_balance;
    }
    const monthKeys = Object.keys(debt.current_by_month || {}).sort();
    return monthKeys.length > 0 
      ? debt.current_by_month[monthKeys[monthKeys.length - 1]] 
      : (debt.original_amount || 0);
  };

  // Get all payment transactions for a specific debt up to the viewed month
  const getDebtPayments = (debtId) => {
    return getTransactionsUpToViewMonth(transactions || [])
      .filter(t => t.is_debt_payment && t.debt_id === debtId)
      .sort((a, b) => new Date(b.date) - new Date(a.date)); // Most recent first
  };

  // Only funds switched on under "Count in net worth" (fund page) are included.
  const netWorthFunds = getNetWorthFunds(sinkingFunds);
  const excludedFundCount = countExcludedFunds(sinkingFunds);
  const totalSinkingFunds = netWorthFunds.reduce((sum, f) => sum + getFundBalance(f.id), 0);
  const totalInvestments = investments.reduce((sum, inv) => sum + getInvestmentBalance(inv.id), 0);
  const totalAssets = assets.reduce((sum, a) => sum + getCurrentForMonth(a), 0);
  const totalDebts = debts.reduce((sum, d) => sum + getDebtBalance(d), 0);

  // Calculate cash available from budget (same formula as BudgetTab)
  // Formula: (leftover from last month + income excluding interest + savings withdrawals) - spent - savings deposits
  const currentMonthTransactions = (transactions || []).filter(t => t.month_key === monthKey);
  const expenseTransactionsForMonth = currentMonthTransactions.filter(t => !t.is_income && !t.is_savings);
  // Exclude interest income - it goes directly to savings/investment accounts
  const incomeTransactionsForMonth = currentMonthTransactions.filter(t => t.is_income && !t.is_from_savings && !t.is_interest);
  const savingsTransactionsForMonth = currentMonthTransactions.filter(t => t.is_savings);
  const totalSpentForMonth = expenseTransactionsForMonth.reduce((sum, t) => sum + t.amount, 0);
  const totalIncomeForMonth = incomeTransactionsForMonth.reduce((sum, t) => sum + t.amount, 0);
  // Separate deposits from withdrawals in savings transactions
  const totalSavingsDepositsForMonth = savingsTransactionsForMonth
    .filter(t => t.transaction_type !== 'withdrawal')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalSavingsWithdrawalsForMonth = savingsTransactionsForMonth
    .filter(t => t.transaction_type === 'withdrawal')
    .reduce((sum, t) => sum + t.amount, 0);
  const leftoverFromLastMonth = budgetSettings?.leftover_by_month?.[monthKey] || 0;
  // Cash Available = Leftover + Income (excluding interest) + Withdrawals from Savings - Spent - Deposits to Savings
  const cashAvailable = (leftoverFromLastMonth + totalIncomeForMonth + totalSavingsWithdrawalsForMonth) - totalSpentForMonth - totalSavingsDepositsForMonth;

  // Calculate projected savings (current + pending extra payments)
  // Only exclude payments that have been moved to accounts
  const getProjectedSavingsForFund = (fundId) => {
    const currentAmount = getFundBalance(fundId);
    
    const pendingPayments = (extraPayments || [])
      .filter(p => !p.moved_to_account && countsInProjection(p, viewMonth.getFullYear()))
      .reduce((sum, p) => {
        if (p.fund_allocations && p.fund_allocations[fundId]) {
          return sum + signOf(p) * p.fund_allocations[fundId];
        }
        const fundsList = p.designated_funds || (p.designated_fund ? [p.designated_fund] : []);
        if (fundsList.includes(fundId)) {
          return sum + signOf(p) * ((p.amount || 0) / fundsList.length);
        }
        return sum;
      }, 0);
    return currentAmount + pendingPayments;
  };

  const projectedSavings = netWorthFunds.reduce((sum, f) => sum + getProjectedSavingsForFund(f.id), 0);

  // Calculate projected investments (current + pending extra payments)
  const getProjectedInvestmentForAccount = (invId) => {
    const currentAmount = getInvestmentBalance(invId);
    
    const pendingPayments = (extraPayments || [])
      .filter(p => !p.moved_to_account && countsInProjection(p, viewMonth.getFullYear()))
      .reduce((sum, p) => {
        if (p.investment_allocations && p.investment_allocations[invId]) {
          return sum + p.investment_allocations[invId];
        }
        return sum;
      }, 0);
    return currentAmount + pendingPayments;
  };

  const projectedInvestments = investments.reduce((sum, inv) => sum + getProjectedInvestmentForAccount(inv.id), 0);

  // Projected net worth = projected savings + projected investments + cash available + assets - debts
  const projectedNetWorth = projectedSavings + projectedInvestments + cashAvailable + totalAssets - totalDebts;

  const totalPositive = totalSinkingFunds + totalInvestments + cashAvailable + totalAssets;
  const netWorth = totalPositive - totalDebts;

  // Goals
  const totalSinkingFundsGoal = (sinkingFunds || []).reduce((sum, f) => sum + (f.goal || 0), 0);
  const totalInvestmentsGoal = investments.reduce((sum, inv) => sum + (inv.goal || 0), 0);
  const totalAssetsGoal = assets.reduce((sum, a) => sum + (a.goal || 0), 0);

  const navigateMonth = (direction) => {
    if (embedded && onNavigateMonth) {
      onNavigateMonth(direction);
      setEditingItem(null);
      return;
    }
    setInternalViewMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
    setEditingItem(null);
  };

  // Update investment account
  const updateInvestment = (invId) => {
    const updatedInvestments = investments.map(inv => {
      if (inv.id === invId) {
        return { 
          ...inv, 
          goal: goalInput !== '' ? parseFloat(goalInput) || 0 : inv.goal
        };
      }
      return inv;
    });
    saveUserData({ investment_accounts: updatedInvestments });
    setEditingItem(null);
    setGoalInput('');
  };

  // Set override balance for investment for current month
  const setInvestmentOverride = (invId) => {
    if (overrideBalanceInput === '') return;
    const updatedInvestments = investments.map(inv => (inv.id === invId ? {
      ...inv,
      override_by_month: { ...inv.override_by_month, [monthKey]: parseFloat(String(overrideBalanceInput).replace(/,/g, '')) || 0 },
      override_at_by_month: { ...(inv.override_at_by_month || {}), [monthKey]: manualDayFor(monthKey) }, // dark_13
    } : inv));
    saveUserData({ investment_accounts: updatedInvestments });
    setEditingInvestmentBalance(null);
    setOverrideBalanceInput('');
  };

  // Use calculated: removes only this month's manual balance (dark_14)
  const clearInvestmentOverride = (invId) => {
    const keep = (byMonth) => Object.fromEntries(Object.entries(byMonth || {}).filter(([k]) => k !== monthKey));
    const updatedInvestments = investments.map(inv => (
      inv.id === invId ? { ...inv, override_by_month: keep(inv.override_by_month), override_at_by_month: keep(inv.override_at_by_month) } : inv
    ));
    saveUserData({ investment_accounts: updatedInvestments });
    customToast.success("Using calculated balance");
  };

  // Update asset
  const updateAsset = (assetId) => {
    const updatedAssets = assets.map(a => {
      if (a.id === assetId) {
        const newCurrentByMonth = { ...a.current_by_month };
        if (currentInput !== '') newCurrentByMonth[monthKey] = parseFloat(currentInput) || 0;
        return { 
          ...a, 
          current_by_month: newCurrentByMonth
        };
      }
      return a;
    });
    saveUserData({ assets: updatedAssets });
    setEditingItem(null);
    setCurrentInput('');
  };

  // Update debt - only updates original_amount and starting_balance
  // Current balance is calculated from starting_balance - payments
  const updateDebt = (debtId) => {
    const updatedDebts = debts.map(d => {
      if (d.id === debtId) {
        return { 
          ...d, 
          original_amount: goalInput !== '' ? parseFloat(goalInput) || 0 : d.original_amount,
          starting_balance: currentInput !== '' ? parseFloat(currentInput) || 0 : getDebtStartingBalance(d)
        };
      }
      return d;
    });
    saveUserData({ debts: updatedDebts });
    setEditingItem(null);
    setGoalInput('');
    setCurrentInput('');
  };

  // Add new asset
  const addAsset = () => {
    if (newAsset.name.trim()) {
      const asset = {
        id: `asset_${Date.now()}`,
        name: newAsset.name.trim(),
        goal: parseFloat(newAsset.goal) || 0,
        current_by_month: { [monthKey]: parseFloat(newAsset.current) || 0 }
      };
      saveUserData({ assets: [...assets, asset] });
      setNewAsset({ name: '', goal: '', current: '' });
      setShowAddAsset(false);
    }
  };

  // Add new debt
  const addDebt = () => {
    if (newDebt.name.trim()) {
      const debt = {
        id: `debt_${Date.now()}`,
        name: newDebt.name.trim(),
        original_amount: parseFloat(newDebt.original) || 0,
        starting_balance: parseFloat(newDebt.current) || 0
      };
      saveUserData({ debts: [...debts, debt] });
      setNewDebt({ name: '', original: '', current: '' });
      setShowAddDebt(false);
    }
  };

  // Delete asset/debt
  const deleteAsset = (id) => { saveUserData({ assets: assets.filter(a => a.id !== id) }); };
  const deleteDebt = (id) => { saveUserData({ debts: debts.filter(d => d.id !== id) }); };

  return (
    <div className={`max-w-4xl mx-auto relative${plan ? ' wy-plan-nw' : ''}`} style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* New Hero Section */}
      {showNetWorthInfo && createPortal(
        <div className="fixed inset-0 z-50" style={{ touchAction: 'none' }}>
          <div className="absolute inset-0 bg-black/40" onClick={closeNetWorthInfo} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-10 shadow-xl">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
            <h3 className="font-normal text-gray-800 mb-4" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '20px' }}>About Net Worth</h3>
            <div className="space-y-4">
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">📈</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Your financial snapshot</p><p className="text-xs text-gray-500 leading-relaxed">Net worth is your total assets minus your liabilities, updated automatically as you log transactions in the Budget tab.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">🏦</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Assets & liabilities</p><p className="text-xs text-gray-500 leading-relaxed">Cards, savings funds, and investment accounts count toward your assets. Debts are set up during onboarding but can be updated in Settings — you can set an original and starting balance. As you log debt payments in the Budget tab, your balance updates here.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">📅</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Trend Chart</p><p className="text-xs text-gray-500 leading-relaxed">See how your net worth has changed month by month over time.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">🎯</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Milestones</p><p className="text-xs text-gray-500 leading-relaxed">You'll be notified when you hit a new net worth milestone, if you have push notifications turned on.</p></div></div>
            </div>
            <button onClick={closeNetWorthInfo} className="mt-6 w-full py-3 rounded-2xl theme-bg-500 text-white font-semibold text-sm">Got it</button>
          </div>
        </div>,
        document.body
      )}
      <NetWorthHero
        theme={theme}
        hideTitle={embedded}
        viewMonth={viewMonth}
        onPrevMonth={() => navigateMonth(-1)}
        onNextMonth={() => navigateMonth(1)}
        canViewHistory={canViewHistory}
        showFeaturePaywall={showFeaturePaywall}
        netWorth={netWorth}
        totalAssets={totalPositive}
        totalDebts={totalDebts}
        projectedNetWorth={projectedNetWorth}
        onInfoClick={() => (onShowInfo ? onShowInfo() : setInternalShowNetWorthInfo(true))}
        monthChange={isCurrentMonth && previousNetWorth !== null && previousNetWorth !== undefined ? netWorth - previousNetWorth : null} // plan_11
        path={plan ? <WorthPath netWorth={netWorth} growth={worthGrow} note={!planGrow && worthGrow ? 'Dates are an estimate based on your last 6 months' : null} /> : null} // plan_22, w2_04
        chart={plan ? (
          <PremiumFeature feature="netWorthTrends">
            <NetWorthChartFromMonthly
              sinkingFunds={sinkingFunds}
              investments={investments}
              assets={assets}
              debts={debts}
              extraPayments={extraPayments}
              transactions={transactions}
              budgetSettings={budgetSettings}
              theme={theme}
            />
          </PremiumFeature>
        ) : null}
      />

      {/* Net Worth Trend Chart */}
      <div className="px-4">
        {!plan && (
        <PremiumFeature feature="netWorthTrends" className="mb-4">
          <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}><span className="wy-star">✦ </span>{'Net worth trend'}</p>
          <NetWorthChartFromMonthly
            sinkingFunds={sinkingFunds}
            investments={investments}
            assets={assets}
            debts={debts}
            extraPayments={extraPayments}
            transactions={transactions}
            budgetSettings={budgetSettings}
            theme={theme}
          />
        </PremiumFeature>
        )}

        {plan ? (
          // plan_09: Savings, Investments and Cash available as rows in one card
          <div style={{ background: '#FFFFFF', borderRadius: 24, padding: '6px 18px', marginTop: 12 }} data-testid="plan-networth-rows">
            {[
              { emoji: '\u{1F437}', bg: 'linear-gradient(135deg, #F6C5D8, #FCE6EF)', name: 'Savings', sub: excludedFundCount > 0 ? `${excludedFundCount} ${excludedFundCount === 1 ? 'fund' : 'funds'} not counted` : 'From your funds', subId: excludedFundCount > 0 ? 'networth-excluded-funds' : undefined, value: totalSinkingFunds, color: '#2F8756' },
              { emoji: '\u{1F4C8}', bg: 'linear-gradient(135deg, #D3C6F5, #ECE6FC)', name: 'Investments', sub: 'From Invest', value: totalInvestments, color: '#3C6DB5' },
              { emoji: '\u{1F4B5}', bg: 'linear-gradient(135deg, #BFE6CF, #E3F5EA)', name: 'Cash available', sub: 'From Budget', value: cashAvailable, color: cashAvailable < 0 ? '#C04B7C' : '#26252E' },
            ].map((r, i) => (
              <div key={r.name} className="flex items-center" style={{ gap: 12, padding: '12px 0', borderTop: i ? '1px solid #EEEAF6' : 0 }}>
                <span className="flex items-center justify-center flex-shrink-0" style={{ width: 36, height: 36, borderRadius: 12, background: r.bg, fontSize: 17 }}>{r.emoji}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#26252E' }}>{r.name}</span>
                  <span style={{ display: 'block', fontSize: 12.5, color: '#8B879A' }} data-testid={r.subId}>{r.sub}</span>
                </span>
                <span style={{ fontSize: 15, fontWeight: 600, color: r.color, flexShrink: 0 }}>{formatCurrency(r.value)}</span>
              </div>
            ))}
          </div>
        ) : (
        <>
          {/* Total Savings from Sinking Funds */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-3">
            <div className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isPink ? 'bg-pink-50' : isWarm ? 'bg-[#E8F0E8]' : 'bg-indigo-50'}`}>
                  <span className="text-xl">🐷</span>
                </div>
                <div>
                  <h3 className="text-gray-800 text-[15px]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>Savings</h3>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                    isPink ? 'bg-pink-100 text-pink-600' : isWarm ? 'bg-[#E8F0E8] text-[#2D5A3D]' : 'bg-indigo-100 text-indigo-600'
                  }`}>From Wealth Tab</span>
                  {excludedFundCount > 0 && (
                    <p className="text-[11px] text-gray-400 mt-1" data-testid="networth-excluded-funds">
                      {excludedFundCount} {excludedFundCount === 1 ? 'fund' : 'funds'} not counted
                    </p>
                  )}
                </div>
              </div>
              <span className={`text-base font-bold ${isWarm ? 'text-[#2D5A3D]' : 'text-emerald-500'}`}>
                {formatCurrency(totalSinkingFunds)}
              </span>
            </div>
          </div>

          {/* Investments Summary */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-3">
            <div className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isPink ? 'bg-pink-50' : isWarm ? 'bg-[#E8F0E8]' : 'bg-indigo-50'}`}>
                  <span className="text-xl">📊</span>
                </div>
                <div>
                  <h3 className="text-gray-800 text-[15px]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>Investments</h3>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                    isPink ? 'bg-pink-100 text-pink-600' : isWarm ? 'bg-[#E8F0E8] text-[#2D5A3D]' : 'bg-indigo-100 text-indigo-600'
                  }`}>From Wealth Tab</span>
                </div>
              </div>
              <span className={`text-base font-bold ${isWarm ? 'text-[#2D5A3D]' : 'text-blue-500'}`}>
                {formatCurrency(totalInvestments)}
              </span>
            </div>
          </div>

          {/* Cash Available */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-3">
            <div className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isPink ? 'bg-pink-50' : isWarm ? 'bg-[#E8F0E8]' : 'bg-indigo-50'}`}>
                  <span className="text-xl">💵</span>
                </div>
                <div>
                  <h3 className="text-gray-800 text-[15px]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>Cash Available</h3>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                    isPink ? 'bg-pink-100 text-pink-600' : isWarm ? 'bg-[#E8F0E8] text-[#2D5A3D]' : 'bg-indigo-100 text-indigo-600'
                  }`}>From Budget Tab</span>
                </div>
              </div>
              <span className="text-base font-bold theme-text-500">
                {formatCurrency(cashAvailable)}
              </span>
            </div>
          </div>

        </>
        )}

        {/* Assets Section */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-3">
          <div className="p-3 flex items-center justify-between border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isPink ? 'bg-pink-50' : isWarm ? 'bg-[#E8F0E8]' : 'bg-indigo-50'}`}>
                <span className="text-xl">🏠</span>
              </div>
              <h3 className="text-gray-800 text-[15px]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>Assets</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-base font-bold theme-text-500">{formatCurrency(totalAssets)}</span>
              <Button 
                onClick={() => setShowAddAsset(!showAddAsset)} 
                size="sm" 
                className={`h-8 w-8 p-0 rounded-full ${
                  isPink ? 'bg-pink-500 hover:bg-pink-600' : isWarm ? 'bg-[#2D5A3D] hover:bg-[#1B4027]' : 'bg-indigo-500 hover:bg-indigo-600'
                } text-white`}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {showAddAsset && (
            <div className={`p-3 border-b border-gray-100 ${isWarm ? 'bg-[#F8FAF8]' : 'bg-gray-50'}`}>
              <div className="space-y-2">
                <Input 
                  placeholder="Asset name (e.g., Car, Property)" 
                  value={newAsset.name} 
                  onChange={(e) => setNewAsset(p => ({ ...p, name: e.target.value }))} 
                  className="h-9 text-sm border-gray-200"
                />
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">{getCurrentCurrency().symbol}</span>
                  <Input 
                    type="text" 
                    inputMode="decimal" 
                    placeholder="Current value" 
                    value={newAsset.current} 
                    onChange={(e) => setNewAsset(p => ({ ...p, current: e.target.value }))} 
                    className="pl-8 h-9 text-sm border-gray-200"
                  />
                </div>
                <Button 
                  onClick={addAsset} 
                  className={`w-full text-white h-8 text-sm ${
                    isPink ? 'bg-pink-500 hover:bg-pink-600' : isWarm ? 'bg-[#2D5A3D] hover:bg-[#1B4027]' : 'bg-indigo-500 hover:bg-indigo-600'
                  }`}
                >
                  Add Asset
                </Button>
              </div>
            </div>
          )}
          
          {assets.length === 0 ? (
            <div className="p-6 text-center text-gray-400">
              <Building2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-xs">No assets added yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {assets.map(asset => {
                const currentAmount = getCurrentForMonth(asset);
                const isEditing = editingItem === `asset_${asset.id}`;
                
                return (
                  <div key={asset.id} className="p-3 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700 text-sm">{asset.name}</span>
                      <div className="flex items-center gap-2">
                        {!isEditing && (
                          <>
                            <span className="text-base font-bold theme-text-500">
                              {formatCurrency(currentAmount)}
                            </span>
                            <button
                              onClick={() => {
                                setEditingItem(`asset_${asset.id}`);
                                setCurrentInput(currentAmount.toString());
                              }}
                              className="p-1.5 text-gray-400 transition-colors hover:theme-text-500"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => deleteAsset(asset.id)} className="p-1.5 text-gray-300 hover:text-red-500 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {isEditing && (
                      <div className="mt-2 space-y-2">
                        <div>
                          <label className="text-xs text-gray-500">Current Value ({viewMonth.toLocaleDateString('en-US', { month: 'short' })})</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">{getCurrentCurrency().symbol}</span>
                            <Input 
                              type="text"
                              inputMode="decimal"
                              value={currentInput} 
                              onChange={(e) => setCurrentInput(e.target.value)}
                              className="pl-8 h-9 text-sm border-gray-200"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            onClick={() => updateAsset(asset.id)} 
                            className={`text-white h-8 text-sm ${
                              isPink ? 'bg-pink-500 hover:bg-pink-600' : isWarm ? 'bg-[#2D5A3D] hover:bg-[#1B4027]' : 'bg-indigo-500 hover:bg-indigo-600'
                            }`}
                          >
                            <Check className="w-3.5 h-3.5 mr-1" /> Save
                          </Button>
                          <Button variant="outline" onClick={() => { setEditingItem(null); setCurrentInput(''); }} className="h-8 text-sm">
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Debts Section */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4">
          <div className="p-3 flex items-center justify-between border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isPink ? 'bg-pink-50' : isWarm ? 'bg-[#E8F0E8]' : 'bg-indigo-50'}`}>
                <span className="text-xl">🧾</span>
              </div>
              <div className="flex items-center gap-2">
                <h3 className="text-gray-800 text-[15px]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>Debts & Liabilities</h3>
                <div className="relative group">
                  <button className="w-4 h-4 rounded-full bg-gray-100 text-gray-500 text-[10px] font-bold flex items-center justify-center hover:bg-gray-200 transition-colors">
                    i
                  </button>
                  <div className="absolute top-full left-0 mt-1 hidden group-hover:block w-56 p-2 bg-white text-gray-700 border border-gray-200 text-[10px] rounded-lg shadow-lg z-50">
                    <div className="absolute top-0 left-4 transform -translate-y-1/2 rotate-45 w-2 h-2 bg-white border-l border-t border-gray-200"></div>
                    <p className="font-semibold mb-1">How debt balance is calculated:</p>
                    <p className="mb-1">Current Balance = Starting Balance − Payments</p>
                    <p className="text-gray-500">Your starting balance is what you owed when you began tracking. Each debt payment you log in the Budget tab automatically reduces this balance.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-base font-bold text-red-500">-{formatCurrency(totalDebts)}</span>
              <Button 
                onClick={() => setShowAddDebt(!showAddDebt)} 
                size="sm" 
                className={`h-8 w-8 p-0 rounded-full ${
                  isPink ? 'bg-pink-500 hover:bg-pink-600' : isWarm ? 'bg-[#2D5A3D] hover:bg-[#1B4027]' : 'bg-indigo-500 hover:bg-indigo-600'
                } text-white`}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {showAddDebt && (
            <div className={`p-3 border-b border-gray-100 ${isWarm ? 'bg-[#F8FAF8]' : 'bg-gray-50'}`}>
              <div className="space-y-2">
                <Input 
                  placeholder="Debt name (e.g., Student Loan, Car Loan)" 
                  value={newDebt.name} 
                  onChange={(e) => setNewDebt(p => ({ ...p, name: e.target.value }))} 
                  className="h-9 text-sm border-gray-200"
                />
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">{getCurrentCurrency().symbol}</span>
                    <Input 
                      type="text" 
                      inputMode="decimal" 
                      placeholder="Original amount" 
                      value={newDebt.original} 
                      onChange={(e) => setNewDebt(p => ({ ...p, original: e.target.value }))} 
                      className="pl-8 h-9 text-sm border-gray-200"
                    />
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">{getCurrentCurrency().symbol}</span>
                    <Input 
                      type="text" 
                      inputMode="decimal" 
                      placeholder="Starting balance" 
                      value={newDebt.current} 
                      onChange={(e) => setNewDebt(p => ({ ...p, current: e.target.value }))} 
                      className="pl-8 h-9 text-sm border-gray-200"
                    />
                  </div>
                </div>
                <Button 
                  onClick={addDebt} 
                  className={`w-full text-white h-8 text-sm ${
                    isPink ? 'bg-pink-500 hover:bg-pink-600' : isWarm ? 'bg-[#2D5A3D] hover:bg-[#1B4027]' : 'bg-indigo-500 hover:bg-indigo-600'
                  }`}
                >
                  Add Debt
                </Button>
              </div>
            </div>
          )}
          
          {debts.length === 0 ? (
            <div className="p-6 text-center text-gray-400">
              <CreditCard className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-xs">No debts - great job!</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {debts.map(debt => {
                const currentAmount = getDebtBalance(debt);
                const goal = debt.original_amount;
                const debtPaidPercent = goal > 0 ? Math.min(((goal - currentAmount) / goal) * 100, 100) : 0;
                const isPaidOff = currentAmount <= 0;
                const debtStartingBalance = getDebtStartingBalance(debt);
                const debtTotalPayments = getDebtTotalPayments(debt.id);

                return (
                  <div key={debt.id} className="p-3 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700 text-sm">{debt.name}</span>
                        {isPaidOff && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-600 text-[10px] font-semibold rounded-full">
                            Paid Off!
                          </span>
                        )}
                      </div>
                      <button onClick={() => deleteDebt(debt.id)} className="p-1.5 text-gray-300 hover:text-red-500 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Debt Details */}
                    <div className="bg-red-50/50 rounded-xl p-3 mb-2 space-y-1.5">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Original Loan</span>
                        <span className="font-medium text-gray-700">{formatCurrency(goal)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Starting Balance</span>
                        <span className="font-medium text-gray-700">{formatCurrency(debtStartingBalance)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Total Paid</span>
                        <span className="font-medium text-green-600">-{formatCurrency(debtTotalPayments)}</span>
                      </div>
                      <div className="border-t border-red-200 pt-1.5 flex justify-between text-sm">
                        <span className="font-medium text-gray-700">Current Balance</span>
                        <span className="font-bold text-red-500">{formatCurrency(currentAmount)}</span>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-gray-500">{debtPaidPercent.toFixed(0)}% paid off</span>
                      {debtTotalPayments > 0 && (
                        <button
                          onClick={() => setViewingDebtPayments(viewingDebtPayments === debt.id ? null : debt.id)}
                          className="flex items-center gap-1 hover:opacity-80 transition-opacity"
                          style={{ color: accentColors.primary }}
                        >
                          {viewingDebtPayments === debt.id ? 'Hide' : 'View'} Payments
                        </button>
                      )}
                    </div>
                    <div className="relative h-2 bg-red-100 rounded-full overflow-hidden">
                      <div 
                        className="absolute h-full bg-emerald-500 rounded-full transition-all"
                        style={{ width: `${debtPaidPercent}%` }}
                      />
                    </div>

                    {/* Payments list */}
                    {viewingDebtPayments === debt.id && (
                      <div className="mt-3 rounded-2xl overflow-hidden" style={{ background: isPink ? '#F8F1F5' : isWarm ? '#F3EDE4' : '#F0F2FC' }}>
                        <div className="px-3 pt-2.5 pb-1.5 flex items-baseline gap-2">
                          <span className="text-[13.5px] font-bold" style={{ color: '#1A1A1F' }}>Payments</span>
                          <span className="flex-1 min-w-0 text-[11px] font-medium truncate" style={{ color: isWarm ? '#6B6459' : '#6B6270' }}>
                            {getDebtPayments(debt.id).length} logged
                          </span>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {getDebtPayments(debt.id).length > 0 ? (
                            getDebtPayments(debt.id).map((payment, i) => (
                              <div
                                key={payment.id}
                                className="flex items-center gap-2.5 px-3 py-2.5"
                                style={{ borderTop: '1px solid rgba(255,255,255,0.85)' }}
                              >
                                <span
                                  className="flex items-center justify-center flex-shrink-0"
                                  style={{ width: '30px', height: '30px', borderRadius: '10px', background: '#FFFFFF', fontSize: '14px' }}
                                >🧾</span>
                                <span className="flex-1 min-w-0">
                                  <span className="block text-[13.5px] font-semibold truncate" style={{ color: '#1F2937' }}>
                                    {payment.description || debt.name}
                                  </span>
                                  <span className="block text-[11px] font-medium truncate" style={{ color: isWarm ? '#6B6459' : '#6B6270', marginTop: '1px' }}>
                                    {new Date(payment.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    {payment.payment_method && payment.payment_method !== 'Debt Payment' ? ` · ${payment.payment_method}` : ''}
                                  </span>
                                </span>
                                <span className="text-[13.5px] font-bold flex-shrink-0" style={{ color: isWarm ? '#2D5A3D' : '#16A34A' }}>
                                  -{formatCurrency(payment.amount)}
                                </span>
                              </div>
                            ))
                          ) : (
                            <p className="px-3 pb-3 text-[12px] font-medium" style={{ color: isWarm ? '#6B6459' : '#6B6270' }}>
                              No payments logged yet.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {/* w2_11: no How it works button here */}
      </div>
    </div>
  );
};

export default NetWorthTab;
